import { ColorTypes } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import Decimal from 'decimal.js';
import { useEffect, useMemo, useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';

import { productsCartState } from '../../../../../../../store/batchTicket/atom';
import {
  AssetDefaultOrSelectedTypeMap,
  SubmitUpdateProps,
} from '../../../../../../../store/product/types';
import { sessionState } from '../../../../../../../store/session/atom';
import { FeatureToggle, useUnleashFlags } from '../../../../../../../utils/useUnleashFlags';

import { FixedIncomeFormData, getValidationCreateSchema } from './constants';

export const useFixedIncomeAssetController = (
  offer: AssetDefaultOrSelectedTypeMap['fixedIncome'],
  submitUpdate?: (data: SubmitUpdateProps) => Promise<boolean>
) => {
  const { userProfile } = useRecoilValue(sessionState);
  const { b2bBlockFeesSpreadSelection } = useUnleashFlags(FeatureToggle.BlockFeesSpreadSelection);
  const isMasterGroup = !!userProfile?.masterGrupo;

  const ableToChangeFees = offer.hasFeesOptions && (!b2bBlockFeesSpreadSelection || isMasterGroup);

  const [isFocused, setIsFocused] = useState(false);
  const [prevValue, setPrevValue] = useState(0);
  const [showMessageAdjustUnitPrice, setShowMessageAdjustUnitPrice] = useState(false);

  const fixedIncomerOffersSelected = useRecoilValue(productsCartState).fixedIncome;

  const fixedIncomeSelectedInBasket = fixedIncomerOffersSelected.items.find(
    (item) => item.productIdNormalized === offer.productIdNormalized
  );

  const defaultValue = useMemo(() => {
    const isBasket = !!submitUpdate;
    return isBasket
      ? offer.amount || offer.applicationMinimumAmount
      : offer.applicationMinimumAmount;
  }, [offer, submitUpdate]);

  const [valueApplied, setValueApplied] = useState(
    fixedIncomeSelectedInBasket?.orderValue || offer.orderValue || defaultValue
  );

  const [selectedFeeId, setSelectedFeeId] = useState(
    fixedIncomeSelectedInBasket?.orderFee?.feeId || offer.orderFee?.feeId || offer.feeId
  );
  const selectedFee = offer.distributionFeesNormalized.find((fee) => fee.feeId === selectedFeeId);

  const valueAppliedFormatted = Utils.Mask.Money(valueApplied);
  const minValueFormatted = Utils.Mask.Money(offer.applicationMinimumAmount);

  const minApplication = useMemo(
    () => selectedFee?.min || offer.applicationMinimumAmount || 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFeeId]
  );

  const maxApplication = useMemo(
    () => selectedFee?.max || offer.applicationMaximumAmount || Number.MAX_SAFE_INTEGER,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFeeId]
  );

  const estimatedIncomeCalculated = useMemo(
    () => ((selectedFee?.commissionFee || 0) / 100) * (valueApplied || 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFeeId, valueApplied]
  );

  const estimatedNetValueCalculated = useMemo(
    () => {
      const value = valueApplied || 0;
      const profitabilityToMaturity =
        selectedFee?.profitabilityToMaturity || offer.profitabilityToMaturity || 0;
      const estimatedRentabilityValue = value * (profitabilityToMaturity / 100);
      const estimatedTaxValue = estimatedRentabilityValue * ((offer.incomeTaxAmount || 0) / 100);

      return value + (estimatedRentabilityValue - estimatedTaxValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFeeId, valueApplied]
  );

  const resolver = useMemo(
    () =>
      zodResolver(
        getValidationCreateSchema(
          selectedFee?.min || offer.applicationMinimumAmount || 0,
          selectedFee?.max || offer.applicationMaximumAmount || Number.MAX_SAFE_INTEGER
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [minApplication, maxApplication]
  );

  const tableForm = useForm<FixedIncomeFormData>({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: {
      createAppliedAmount: valueAppliedFormatted,
    },
    resolver,
  });

  const isAmountWithinRange = (amount: number) => {
    return (
      minApplication &&
      maxApplication &&
      prevValue !== amount &&
      amount >= minApplication &&
      amount <= maxApplication
    );
  };

  const isMultipleOf = (value: number, multiple: number): boolean => {
    return value % multiple === 0;
  };

  const getMultipleValue = (value: number, multiple: number): number => {
    const decimalValue = Decimal(value);
    const decimalMultiple = Decimal(multiple);
    const quotient = decimalValue.dividedBy(decimalMultiple).floor();
    const multipleValue = quotient.times(decimalMultiple);
    const minApplicationDecimal = Decimal(minApplication);

    if (multipleValue.lessThan(minApplicationDecimal)) {
      return minApplication;
    }
    return multipleValue.toNumber();
  };

  const updateAppliedAmount = async (
    amount: number,
    isMultiple: boolean,
    preventUpdate = false
  ) => {
    const updateValues = (newAmount: number) => {
      const newFormattedAmount = Utils.Mask.Money(newAmount);
      tableForm.setValue('createAppliedAmount', newFormattedAmount);
      setValueApplied(newAmount);
      setPrevValue(newAmount);
    };

    if (submitUpdate && !preventUpdate) {
      const response = await submitUpdate({ amount });
      if (response) {
        updateValues(amount);
      } else {
        updateValues(prevValue);
      }
    } else {
      updateValues(amount);
    }

    if (!isMultiple) {
      setShowMessageAdjustUnitPrice(true);
    }
  };

  const processInputAmount = (unformattedAmount: number, preventUpdate = false) => {
    const unitPrice = Number(selectedFee?.customerPuAmount || offer.unitPrice || 0);
    const isMultiple = isMultipleOf(unformattedAmount, unitPrice);
    const adjustedAmount = getMultipleValue(unformattedAmount, unitPrice);

    if (isAmountWithinRange(unformattedAmount) && prevValue !== unformattedAmount) {
      updateAppliedAmount(adjustedAmount, isMultiple, preventUpdate);
    }
  };

  const handleInputBlur = () => {
    const currentValue = tableForm.getValues('createAppliedAmount');
    const currentValueUnformatted = Utils.Unmask.Money(currentValue);

    if (currentValue !== minValueFormatted) {
      processInputAmount(currentValueUnformatted);
    }

    if (isFocused) {
      setIsFocused(false);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setShowMessageAdjustUnitPrice(false);
    setPrevValue(offer?.applicationMinimumAmount || 0);

    if (tableForm.formState.errors?.createAppliedAmount) {
      tableForm.resetField('createAppliedAmount');
    }
  };

  const handleInputClear = () => {
    setIsFocused(false);
    tableForm.resetField('createAppliedAmount');
    tableForm.clearErrors('createAppliedAmount');
  };

  const calculateFinalAmount = (amount: number, unitPrice: number) => {
    if (isMultipleOf(amount, unitPrice)) {
      return amount;
    }
    return getMultipleValue(amount, unitPrice);
  };

  const handleSubmit = async ({ createAppliedAmount }: FixedIncomeFormData) => {
    const unformattedAmount = Utils.Unmask.Money(createAppliedAmount);
    const unitPrice = Number(selectedFee?.customerPuAmount || offer.unitPrice || 0);
    const finalAmount = calculateFinalAmount(unformattedAmount, unitPrice);

    const updateFormValues = (amount: number) => {
      tableForm.setValue('createAppliedAmount', Utils.Mask.Money(amount));
      setValueApplied(amount);
    };

    const shouldShowAdjustUnitPriceMessage = () => {
      return !isMultipleOf(unformattedAmount, unitPrice);
    };

    if (submitUpdate) {
      const response = await submitUpdate({ amount: finalAmount });

      if (response) {
        updateFormValues(finalAmount);

        if (shouldShowAdjustUnitPriceMessage()) {
          setShowMessageAdjustUnitPrice(true);
        }
      } else {
        updateFormValues(prevValue);
      }
    } else {
      updateFormValues(finalAmount);

      if (shouldShowAdjustUnitPriceMessage()) {
        setShowMessageAdjustUnitPrice(true);
      }
    }

    setIsFocused(false);
  };

  const handleSelectNewFee = (feeId: number) => {
    const newSelectedFeeId = offer.distributionFeesNormalized.find((fee) => fee.feeId === feeId);
    setSelectedFeeId(feeId);
    if (!newSelectedFeeId) {
      return;
    }
    const newAmount = getMultipleValue(valueApplied || 0, newSelectedFeeId.customerPuAmount);
    if (submitUpdate) {
      submitUpdate({
        requestedOfferDetail: { ...newSelectedFeeId },
        amount: newAmount,
      });
    }
  };

  const helpTextWhenIsFocused = showMessageAdjustUnitPrice
    ? 'Ajustado por preço unitário'
    : `Mínimo: ${Utils.Mask.Money(minApplication)}`;

  const messageAdjustUnitPriceColor = {
    color: (isFocused ? 'brand.tertiary.principal' : 'neutral.light') as ColorTypes.Option,
  };

  const productsDetailText = 'Veja mais detalhes\nsobre este produto';

  useEffect(() => {
    if (!selectedFee || !valueApplied) {
      return;
    }
    const newAmount = getMultipleValue(valueApplied, selectedFee.customerPuAmount);
    processInputAmount(newAmount, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeeId]);

  return {
    handleInputFocus,
    handleInputClear,
    isFocused,
    handleInputBlur,
    handleSubmit: tableForm.handleSubmit(handleSubmit),
    control: tableForm.control as unknown as Control,
    showMessageAdjustUnitPrice,
    helpTextProps: messageAdjustUnitPriceColor,
    helpText: helpTextWhenIsFocused,
    hasError: tableForm.formState.errors?.createAppliedAmount,
    currentValue: valueApplied,
    currentValueFormatted: valueAppliedFormatted,
    productsDetailText,
    ableToChangeFees,
    selectedFee,
    selectedFeeId,
    setSelectedFeeId: handleSelectNewFee,
    minApplication,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  };
};

export type FixedIncomeAssetController = ReturnType<typeof useFixedIncomeAssetController>;
